import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { resolveAsset } from "../lib/api";

/**
 * Coverflow-style photo carousel.
 *
 * Renders three visible slides at any time: the centred "active" photo and
 * its previous/next siblings tucked behind it. Tapping/clicking a side
 * slide jumps to it; tapping the active photo opens a full-screen
 * lightbox. Keyboard arrows nudge the active slide left/right when the
 * carousel is focused or the lightbox is open. The whole thing is
 * presentational — pure CSS transforms, no third-party carousel lib —
 * because the design lives or dies on getting the depth + slide animation
 * exactly right.
 */
export default function PhotoCarousel({ images = [], dataTestId = "photo-carousel" }) {
  const [active, setActive] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const containerRef = useRef(null);

  const total = images.length;
  const safeActive = total === 0 ? 0 : ((active % total) + total) % total;

  const go = (delta) => {
    if (total === 0) return;
    setActive((i) => ((i + delta) % total + total) % total);
  };

  // Keyboard support — only active when the carousel/lightbox is in focus,
  // so we don't hijack arrow keys for the rest of the page.
  useEffect(() => {
    const onKey = (e) => {
      if (lightboxOpen) {
        if (e.key === "Escape") setLightboxOpen(false);
        if (e.key === "ArrowRight") go(1);
        if (e.key === "ArrowLeft") go(-1);
        return;
      }
      if (!containerRef.current?.contains(document.activeElement)) return;
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxOpen, total]);

  if (total === 0) {
    return (
      <div
        className="flex h-64 items-center justify-center rounded-md border border-dashed border-black/15 text-sm text-[var(--brand-body)]"
        data-testid={`${dataTestId}-empty`}
      >
        No photos in this carousel yet.
      </div>
    );
  }

  // Compute relative offset of each slide from the active one in [-2..2].
  // Slides further out are hidden so we never render hundreds of nodes.
  const slideOffset = (idx) => {
    const half = Math.floor(total / 2);
    let d = idx - safeActive;
    if (d > half) d -= total;
    if (d < -half) d += total;
    return d;
  };

  const activeImage = images[safeActive];

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      className="relative w-full focus:outline-none"
      data-testid={dataTestId}
      aria-roledescription="carousel"
    >
      {/* Stage */}
      <div className="relative mx-auto flex h-[420px] w-full max-w-5xl items-center justify-center overflow-hidden sm:h-[520px]">
        {images.map((img, idx) => {
          const off = slideOffset(idx);
          if (Math.abs(off) > 2) return null;
          // Visual styling: active card is centred + sharp; off-by-one cards
          // are pushed sideways, scaled down, and blurred to mimic depth.
          const isActive = off === 0;
          const baseShift = 58; // px per step on mobile-ish widths
          const x = off * baseShift;
          const scale = isActive ? 1 : 0.78 - 0.06 * (Math.abs(off) - 1);
          const blurAmount = isActive ? 0 : 4 + 4 * (Math.abs(off) - 1);
          const opacity = Math.abs(off) > 1 ? 0.5 : 1;
          const z = 10 - Math.abs(off);
          return (
            <button
              key={img.id || idx}
              type="button"
              onClick={() => {
                if (isActive) setLightboxOpen(true);
                else setActive(idx);
              }}
              aria-label={
                isActive
                  ? `Open photo full size${img.caption ? `: ${img.caption}` : ""}`
                  : `Go to photo ${idx + 1}${img.caption ? `: ${img.caption}` : ""}`
              }
              data-testid={`${dataTestId}-slide-${idx}`}
              data-active={isActive ? "true" : "false"}
              className="absolute top-1/2 left-1/2 block h-full aspect-[3/4] -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-[transform,filter,opacity] duration-500 ease-out focus:outline-none"
              style={{
                transform: `translate(calc(-50% + ${x}%), -50%) scale(${scale})`,
                filter: `blur(${blurAmount}px)`,
                opacity,
                zIndex: z,
              }}
            >
              <div
                className={`relative h-full w-full overflow-hidden rounded-2xl bg-black/5 ${
                  isActive ? "ring-1 ring-black/10 shadow-2xl" : ""
                }`}
              >
                <img
                  src={resolveAsset(img.url)}
                  alt={img.caption || ""}
                  loading={Math.abs(off) <= 1 ? "eager" : "lazy"}
                  className="h-full w-full object-cover"
                  draggable={false}
                />
                {isActive ? (
                  <span
                    className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-[#0A0F17]/85 px-4 py-1 font-display text-xs font-bold uppercase tracking-wider text-white"
                    data-testid={`${dataTestId}-open-hint`}
                  >
                    Open
                  </span>
                ) : null}
              </div>
            </button>
          );
        })}

        {/* Arrows */}
        {total > 1 ? (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              data-testid={`${dataTestId}-prev`}
              aria-label="Previous photo"
              className="absolute left-2 top-1/2 z-20 -translate-y-1/2 rounded-full border border-black/15 bg-white/85 p-3 text-[var(--brand-ink)] backdrop-blur transition-colors hover:bg-white sm:left-6"
            >
              <ChevronLeft size={20} strokeWidth={2.5} />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              data-testid={`${dataTestId}-next`}
              aria-label="Next photo"
              className="absolute right-2 top-1/2 z-20 -translate-y-1/2 rounded-full border border-black/15 bg-white/85 p-3 text-[var(--brand-ink)] backdrop-blur transition-colors hover:bg-white sm:right-6"
            >
              <ChevronRight size={20} strokeWidth={2.5} />
            </button>
          </>
        ) : null}
      </div>

      {/* Caption + counter */}
      <div className="mt-4 flex flex-col items-center gap-1 px-4 text-center">
        {activeImage?.caption ? (
          <p
            className="font-display text-sm font-semibold text-[var(--brand-ink)] sm:text-base"
            data-testid={`${dataTestId}-caption`}
          >
            {activeImage.caption}
          </p>
        ) : null}
        <p className="label-kicker text-[var(--brand-body)]" data-testid={`${dataTestId}-counter`}>
          {safeActive + 1} / {total}
        </p>
      </div>

      {/* Lightbox */}
      {lightboxOpen ? (
        <div
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/95 p-4"
          data-testid={`${dataTestId}-lightbox`}
          onClick={(e) => {
            if (e.target === e.currentTarget) setLightboxOpen(false);
          }}
        >
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            data-testid={`${dataTestId}-lightbox-close`}
            aria-label="Close full-size photo"
            className="absolute right-4 top-4 rounded-full border border-white/20 bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
          >
            <X size={20} strokeWidth={2.5} />
          </button>

          {total > 1 ? (
            <>
              <button
                type="button"
                onClick={() => go(-1)}
                aria-label="Previous photo"
                data-testid={`${dataTestId}-lightbox-prev`}
                className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-white/10 p-3 text-white transition-colors hover:bg-white/20 sm:left-6"
              >
                <ChevronLeft size={20} strokeWidth={2.5} />
              </button>
              <button
                type="button"
                onClick={() => go(1)}
                aria-label="Next photo"
                data-testid={`${dataTestId}-lightbox-next`}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-white/10 p-3 text-white transition-colors hover:bg-white/20 sm:right-6"
              >
                <ChevronRight size={20} strokeWidth={2.5} />
              </button>
            </>
          ) : null}

          <img
            src={resolveAsset(activeImage.url)}
            alt={activeImage.caption || ""}
            className="max-h-[85vh] max-w-[92vw] rounded-md object-contain"
            data-testid={`${dataTestId}-lightbox-image`}
          />
          {activeImage.caption ? (
            <p className="mt-4 max-w-2xl text-center font-display text-sm text-white/90 sm:text-base">
              {activeImage.caption}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
